import { useEffect, useState, useMemo } from "react";
import { keccak256, encodePacked } from 'viem'

import { _ } from "lodash";

import { useRecord, useRecords } from "@latticexyz/stash/react";
import mudConfig from "contracts/mud.config";
import { stash } from "../../../mud/stash";
import { useSessionPlayer } from "../../../SessionPlayerContext";
import { useSync } from "@latticexyz/store-sync/react";
import { useWorldContract } from "../../../mud/useWorldContract";

import { toWorldTime, formatMultilineForDisplay } from "../../../utils";
import MainButton from "../../../components/MainButton";
import { useGuide } from "../../GuideContext";
import { useLeaderboard } from "../../LeaderboardContext";
import { ClockInterface } from "../../../components/Clock";
import { factionNames } from "../../../config";
import { hasBeenMoreThanADay, progressToADay, timestampToAgeInDays } from "../../../utils.js";

import { SeedViewer } from "../../../components/SeedViewer";

import { textConst, getGuideText, } from "../../../text";

import ExtraAudio from "../../../ExtraAudio.js";

import "../../../assets/styles/state-read.css";

import imgMud1 from "../../../assets/images/mud-1.png";
import imgMud2 from "../../../assets/images/mud-2.png";
import imgMud3 from "../../../assets/images/mud-3.png";
import imgMud4 from "../../../assets/images/mud-4.png";
import imgMud5 from "../../../assets/images/mud-5.png";

const imgMuds = [
  imgMud1,
  imgMud2,
  imgMud3,
  imgMud4,
  imgMud5
]

import imgPin1 from "../../../assets/images/pin-1.png";
import imgPin2 from "../../../assets/images/pin-2.png";
import imgPin3 from "../../../assets/images/pin-3.png";
import imgPin4 from "../../../assets/images/pin-4.png";
import imgPin5 from "../../../assets/images/pin-5.png";

const imgPins = [
  imgPin1,
  imgPin2,
  imgPin3,
  imgPin4,
  imgPin5
]

import imgPin1lrg from "../../../assets/images/pin-1-lrg.png";
import imgPin2lrg from "../../../assets/images/pin-2-lrg.png";
import imgPin3lrg from "../../../assets/images/pin-3-lrg.png";
import imgPin4lrg from "../../../assets/images/pin-4-lrg.png";
import imgPin5lrg from "../../../assets/images/pin-5-lrg.png";
import { filter } from "rxjs";

const imgPinsLrg = [
  imgPin1lrg,
  imgPin2lrg,
  imgPin3lrg,
  imgPin4lrg,
  imgPin5lrg
]

const randomCorners = [];
for( let i = 0; i < 100; i++ ){
  randomCorners.push(`${Math.random() * 5}px ${Math.random() * 5}px ${Math.random() * 5}px ${Math.random() * 5}`);
}

const signIntToString = (value) => {
  let out = '';
  if(value > 0 ){
    out += '+';
  }
  if( value < 0 ){
    out += '-';
  }
  out += Math.abs(value);
  return out;
}

const StoryFull = ({ 
  storyData,  
  hasShownVoteInstruction,
  onShowVoteInstruction = () => {},
  onForkStory = () => {}, 
  onClose = () => {},
  onVoteFail = () => {}
}) => {
  const { address: userAddress } = useSessionPlayer();
  const sync = useSync();
  const worldContract = useWorldContract();

  const [isVoteCast, setIsVoteCast] = useState( false );
  const [isForkForked, setIsForkForked] = useState( false );
  const [isSubmittingVote, setIsSubmittingVote] = useState( false );

  const currentPlayer = (userAddress) ? useRecord({
    stash, 
    table: mudConfig.tables.Player, 
    key: { counterValue: userAddress } 
  }) : false;

  const playerTimes = (userAddress) ? useRecord({
    stash,
    table: mudConfig.tables.PlayerTimes,
    key: { counterValue: userAddress }
  }) : false;

  const now = (new Date()).getTime();
  const voteTimeout = (playerTimes) ? progressToADay( now, parseInt( playerTimes.lastVote ) * 1000 ) : false;
  const isInitiallyVoteTimeout = ( !currentPlayer.hasWritten ) 
    ? 
      false 
    : 
      (playerTimes) 
        ? 
          ! hasBeenMoreThanADay( now, parseInt( playerTimes.lastVote ) * 1000 ) 
        : 
          true;
 
  const currentPlayerFaction = (currentPlayer) ? useRecord({
      stash,
      table: mudConfig.tables.Fraction, 
      key: { counterValue: currentPlayer.fraction }
    })?.fractionName : false;
  
  const author = useRecord({
    stash,
    table: mudConfig.tables.Player,
    key: {counterValue: storyData.author}
  })?.name;
 
  const {guideState, setGuideState} = useGuide();

  const sourceSeed = useRecord({
    stash,
    table: mudConfig.tables.StorySeed,
    key: {counterValue: storyData.orgSeed} // TODO: may not be correct, may need to be key: ??
  });

  const sourceStory = useRecord({
    stash,
    table: mudConfig.tables.StoryPart,
    key: {counterValue: storyData.parent} // TODO: may not be correct
  });

  // const sourceStory = useStore(({getValue}) => {    
  //   const srcStory = getValue( tables.StoryPart, {chash: storyData.parent} );    
  //   return srcStory;
  // })

  const faction = useRecord({
    stash,
    table: mudConfig.tables.Fraction,
    key: {counterValue: storyData.faction} // TODO: may not be correct
  })?.fractionName;

  const storyIsFork = !!useRecord({
    stash,
    table: mudConfig.tables.IsFork,
    key: {counterValue: storyData.counterValue}
  })?.isFork;

  const hasBeenForkedKey = keccak256(encodePacked(['bytes32','address'], [storyData.counterValue, userAddress]));
  const forkRestrictions = useRecord({
    stash,
    table: mudConfig.tables.ForkRestrictions,
    key: {counterValue: hasBeenForkedKey}
  });    
  const storyIsForked = ( forkRestrictions && !!forkRestrictions.forkedBy )
  const isVoted = useRecord({
    stash,
    table: mudConfig.tables.StoryPartVotingRestrictions,
    key: {counterValue: hasBeenForkedKey}
  })
  const isVoteRestricted = (isVoted && !!isVoted.votedOnBy);
 
  const id = storyData.chash;
  const text = storyData.data;
  const votesUp = storyData.posVotes;
  const votesDown = storyData.negVotes;
  const [votes, setVotes] = useState(votesUp - votesDown);
  const parent = storyData.parent;
  const created = toWorldTime( parseInt( storyData.created ) ); /* TODO: convert to a datetime relevant to lore of the world */


  const [ageInDays, setAgeInDays] = useState( 0 );
  const [ageInMonths, setAgeInMonths] = useState(0);
  useEffect( () => {
    const days = timestampToAgeInDays( parseInt( storyData.created ) * 1000 );
    setAgeInDays( (days > 12) ? 12 : days );
    const monthsIsh = Math.floor(days / 30);
    setAgeInMonths( (monthsIsh > 3 ) ? 3 : monthsIsh );
  }, [storyData] );

  // useEffect( () => {
  //   setVotes(storyData.posVotes - storyData.negVotes);
  // }, [storyData] );  

  useEffect(() => {
    if( ! hasShownVoteInstruction && currentPlayer.name !== author){
      setGuideState({
        ...guideState,
        statement: getGuideText( currentPlayerFaction, textConst.VOTEINSTRUCTION )
      })
      onShowVoteInstruction();
    } else {
      setGuideState({
        ...guideState,
        statement: ''
      })
    }
  },[]);

  const addVote = useMemo(
    () => ( sync.data && worldContract ) ? 
      async ( storyId, vote ) => {
        setIsSubmittingVote( true );
        try{
          const tx = await worldContract.write.addVote([storyId, vote]);
          await sync.data.waitForTransaction( tx );
          setIsSubmittingVote( false );
          return 'success';
        } catch(e){
          if(e.shortMessage.indexOf('Smart Account does not have sufficient funds to execute the User Operation') > -1 ){
            return 'funds';
          }
        }
      }
      : undefined,
    [sync.data, worldContract]
  )

  return <article id={id} className={`story-full age-${ageInMonths}`}>
    <div className="story-full--content">      
      <section className="story-full--source">
        {(sourceSeed) ? <SeedViewer seedId={sourceSeed.seed} /> : ''}
      </section>
      <section className="story-full--story">
        <strike>{(sourceStory) ? sourceStory.data : '' }</strike>
        {formatMultilineForDisplay( text )}
      </section>
      <div className="story-full--meta">
        <div>
          {(votes >= 0) ? `+${votes}` : votes}
        </div>
        {/* <div>
          <a href={`#${parent}`}>SOURCE</a><br></br>
        </div> */}
        <div>
          written by {author}, a {factionNames[faction]}
        </div>
      </div>
    </div>
    <section className="story-full--actions">
      {( currentPlayer.hasWritten ) ? 
          <ClockInterface 
            className="vote-timeout-clock"
            progress={voteTimeout}
          />
        : 
          false
      }
      {(currentPlayer.name !== author) ? <MainButton
        name="Gild it"
        onClick={ async () => {
          const voteResult = await addVote( storyData.counterValue, true );

          if( voteResult === 'success' ){
            const statement = getGuideText( currentPlayerFaction, textConst.CLEANING );
            ExtraAudio.sfxSample('gild');
            ExtraAudio.sfxSample( _.kebabCase( statement ));
          
            setIsVoteCast( true );
            setVotes( votes + 1 );
            setGuideState({
              ...guideState,
              statement: statement
            });
          } else if( voteResult === 'funds' ){
            onVoteFail();
          }
        }}
        disabled={ currentPlayer.name === author || isInitiallyVoteTimeout || isVoteCast || isVoteRestricted }
        pending={isSubmittingVote}
      /> : '' }
      {(currentPlayer.name !== author) ? <MainButton
        name="Throw mud"
        onClick={ async () => {
          const voteResult = await addVote( storyData.counterValue, false);
          if( voteResult === 'success' ){
            const statement = getGuideText( currentPlayerFaction, textConst.MUDDING );
            ExtraAudio.sfxSample('mud-sfx');
            ExtraAudio.sfxSample( _.kebabCase( statement ) );
          
            setIsVoteCast( true );
            setVotes( votes - 1 );
            setGuideState({
              ...guideState,
              statement: statement
            });
          } else if( voteResult === 'funds' ){
            onVoteFail();
          }
        }}
        disabled={ currentPlayer.name === author || isInitiallyVoteTimeout || isVoteCast || isVoteRestricted }
        pending={isSubmittingVote}
      /> : '' }         
      {/*(currentPlayerFaction !== 'Wolf' && currentPlayer.name !== author ) 
        ? 
          <MainButton 
            name="Alter story" 
            onClick={ () => { 
              setIsForkForked( true );
              onForkStory();
            }}
            disabled={ currentPlayer.name === author || isInitiallyForkTimeout || isForkForked || storyIsFork || storyIsForked }
          />
        : '' 
      */}
      <MainButton name="Close story" className="story-full--close" onClick={ onClose }/>
    </section>
    <section className="story-full-clock-interface">
      {/* {(isInitiallyVoteTimeout)
        ? <ClockInterface name="Library Timeout" progress={voteTimeout}/>
        : ''
      } */}
    </section>
    <div className="story-full--voteoverlay">
    {imgMuds.map( ( img, i ) => {
        return (votes < 0 && i < Math.abs(votes)) ? <img src={img} className="story-downvoteimg"/> : '';
      })}
      {imgPinsLrg.map(( img, i ) => {
        return (votes > 0 && i < Math.abs(votes)) ? <img src={img} className="story-upvoteimg"/> : '';
      })}
    </div>
  </article>
}

const StoryBlock = ( { storyData, storyIndex=1, onClick = ( s ) => {}  } ) => {
  const author = useRecord({
    stash,
    table: mudConfig.tables.Player,
    key: {counterValue: storyData.author}
  })?.name;

  const players = useRecords({
    stash,
    table: mudConfig.tables.Player
  });
  
  const playersSorted = players
    .filter( ( record ) => {
      return record.wonAt > 0;
    })
    .sort( (a, b) => {
      return parseInt(b.wonAt) - parseInt(a.wonAt);
    });
    

  const secretHolder = ( playersSorted.length > 0 ) ? playersSorted[0] : false;
  const isSecretHolder = secretHolder?.name === author;

  const id = storyData.chash;
  const text = storyData.data;
  const votesUp = storyData.posVotes;
  const votesDown = storyData.negVotes;
  const votes = votesUp - votesDown;

  const [ageInDays, setAgeInDays] = useState( 0 );
  const [ageInMonths, setAgeInMonths] = useState( 0 );
  useEffect( () => {
    const days = timestampToAgeInDays( parseInt( storyData.created ) * 1000 );
    setAgeInDays( (days > 12) ? 12 : days );
    const monthsIsh = Math.floor(days / 30);
    setAgeInMonths( (monthsIsh > 3 ) ? 3 : monthsIsh );
  }, [storyData] );

  const [recentFade, setRecentFade] = useState( 0 );
  useEffect( () => {
    const fadeTime = 5000;
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const age = now - parseInt( storyData.created ) * 1000;
      if( age < fadeTime ){
        setRecentFade( 1 - (age / fadeTime) );
      } else {
        setRecentFade( 0 );
      }
    }, 100 );

    return () => { 
      clearInterval( timer );
    }
  }, [storyData] );

  return <article
    id={id} 
    className={`story-block age-${ageInMonths}${(isSecretHolder) ? ' fango-border__secret': ''}`}
    onClick={ () => {
      ExtraAudio.sfxSample('click');
      onClick( storyData );
    }}
    style={{
      borderRadius: randomCorners[ storyIndex % randomCorners.length ]
    }}
  >
    <section className="story-block--story">
      {formatMultilineForDisplay( text )}
    </section>
    <section className="story-block--meta">
      <div className="meta--seed">
        Chronicle {storyData.orgSeed}
      </div>
      <div className="meta--author">
        {author}
      </div>
    </section>
    <div className="story-full--voteoverlay">
      {imgMuds.map( ( img, i ) => {
        return (votes < 0 && i < Math.abs(votes)) ? <img src={img} className="story-downvoteimg"/> : '';
      })}
      {imgPins.map(( img, i ) => {
        return (votes > 0 && i < Math.abs(votes)) ? <img src={img} className="story-upvoteimg"/> : '';
      })}
    </div>
    <div className="story-block--newfade" style={{opacity: recentFade}}></div>
  </article>
}

export const StateRead = ({shouldRender, onForkStory, onVoteFail = () => {} }) => {
  const { address: userAddress } = useSessionPlayer();
  
  const filterCount = 5;

  const [filterStories, setFilterStories] = useState( false );
  const [viewStory, setViewStory] = useState( false );

  const allStoriesUnsorted = useRecords({
    stash,
    table: mudConfig.tables.StoryPart
  });

  const allStories = Object.values( allStoriesUnsorted ).sort( (a, b) => {
    const votesA = a.posVotes - a.negVotes;
    const votesB = b.posVotes - b.negVotes;
    return votesB - votesA;
  });

  const player = (userAddress) ? useRecord({
    stash,
    table: mudConfig.tables.Player,
    key: { counterValue: userAddress }
  }) : false;

  const [hasShownReadInstruction, setHasShownReadInstruction] = useState((player) ? player.hasWritten : false );
  const [hasShownVoteInstruction, setHasShownVoteInstruction] = useState((player) ? player.hasWritten : false );

  const playerFaction = (player) ? useRecord({
    stash,
    table: mudConfig.tables.Fraction,
    key: { counterValue: player.fraction }
  })?.fractionName : false;

  const players = useRecords({
    stash,
    table: mudConfig.tables.Player
  });
  
  const playersSorted = players
    .filter( ( record ) => {
      return record.wonAt > 0;
    })
    .sort( (a, b) => {
      return parseInt(b.wonAt) - parseInt(a.wonAt);
    });
    

  const secretHolder = ( playersSorted.length > 0 ) ? playersSorted[0] : false;
  
  const secretHolderFaction = useRecord({
    stash,
    table: mudConfig.tables.Fraction,
    key: { counterValue: (secretHolder) ? secretHolder.fraction : false }
  })?.fractionName;

  const FILT = ( filterCount > allStories.length / 2 ) ? Math.floor(allStories.length / 2) : filterCount;
  const stories = (filterStories) ? [ ...allStories.slice(0, FILT), false, ...allStories.slice( -1 * FILT ) ] : allStories;
 
  const {guideState, setGuideState} = useGuide();
  const {leaderboardState, setLeaderboardState} = useLeaderboard();

  useEffect(() => {
    if( shouldRender ){

      setViewStory(false);

      if( !hasShownReadInstruction ){
        setGuideState({
          ...guideState,
          statement: getGuideText( playerFaction, textConst.READINSTRUCTION )
        })
      } else {
        setGuideState({
          ...guideState,
          statement: ''
        })
      }

      setHasShownReadInstruction( true );
    }
  },[ shouldRender ]);

  return <>
    <div
      className="game-state game-state__read"
      style={{
        display: (shouldRender) ? 'block' : 'none'
      }}
    >
      <section className="story-list--container">
        <ul className="story-list">
          {(stories.map( ( story, i ) => {
            return <li key={story.counterValue} className="story-list--entry">
              {( story ) ? <StoryBlock
                storyData={story}
                storyIndex={i}
                onClick={ ( s ) => {
                  ExtraAudio.sfxSample('page');
                  setViewStory( s );
                }}
              /> : <div 
                      className="story-list--filter-indicator"
                      onClick={() => {
                        setFilterStories( !filterStories );
                      }}
                    > 
                    ... 
                  </div>  }
            </li>
          }))}
        </ul>
      </section>
      {( leaderboardState && !viewStory && leaderboardState.ranking.length > 0 )
        ?
          <section className="read-ranking">
            <div className="fango-border-text fango-border fango-border__leader">Memorable storyteller</div>
            <div className="fango-border-text fango-border fango-border__leader">{leaderboardState.ranking[0].author.name}, a {factionNames[leaderboardState.ranking[0].faction.fractionName]} ({signIntToString(leaderboardState.ranking[0].votes)})</div>
            <div className="fango-border-text fango-border fango-border__mud">Despised storyteller</div>
            <div className="fango-border-text fango-border fango-border__mud">{leaderboardState.ranking[leaderboardState.ranking.length-1].author.name}, a {factionNames[leaderboardState.ranking[leaderboardState.ranking.length-1].faction.fractionName]} ({signIntToString(leaderboardState.ranking[leaderboardState.ranking.length-1].votes)})</div>
          </section>
        : ''
      }
      {( secretHolder && !viewStory  )
        ?
          <section className="read-secretholder">
            <div className="fango-border-text fango-border fango-border__secret">Secret holder</div>
            <div className="fango-border-text fango-border fango-border__secret">{secretHolder.name}, a { factionNames[secretHolderFaction]}</div>
          </section>
        : ''
      }
      {(viewStory)
        ? <StoryFull
            storyData={viewStory}
            hasShownVoteInstruction={hasShownVoteInstruction}
            onShowVoteInstruction={ () => {
              setHasShownVoteInstruction( true );
            }}
            onForkStory={ () => {
              onForkStory( viewStory );
            }}
            onClose={ () => {
              setViewStory( false );
            }}
            onVoteFail={onVoteFail}
          />
        : ''
      }
      {(!viewStory && filterCount * 2 < allStories.length)
        ?
          <MainButton className="door-filter-button" name={(filterStories) ? 'Show all' : 'Show best & worst'} onClick={() => {
            setFilterStories( !filterStories );
          }}></MainButton>
        :
          ''
      }
    </div>
  </>
}